import { useMemo } from "react";

import {
  faFile,
  faFileSlash,
  faFiles,
} from "@fortawesome/pro-regular-svg-icons";
import { AxiosError } from "axios";

import { useFile } from "src/api/filesApi/queries";
import {
  BaseFilePill,
  FilePill,
  getFileName,
} from "src/base-components/FilePill";
import { HistorySingleEntry } from "src/cases/caseView/history/HistoryItemFrame";
import { useCase } from "src/cases/queries/cases";
import { CaseHistoryEvent } from "src/cases/queries/casesTypes";
import { GetFileEnvFilesFileIdGetEnvEnum } from "src/clients/files";
import { Pill } from "src/design-system/Pill";
import { useCapabilities } from "src/hooks/useCapabilities";
import { useWorkspaceContext } from "src/router/routerContextHooks";
import { CasePageParams } from "src/router/urls";
import { useParamsDecode } from "src/utils/useParamsDecode";

const DeletedFilePill: React.FC<{
  fileId: string;
  env: GetFileEnvFilesFileIdGetEnvEnum;
}> = ({ fileId, env }) => {
  const { workspace } = useWorkspaceContext();

  const {
    data: file,
    isLoading,
    error,
  } = useFile(fileId, env, workspace.base_url!);

  if (isLoading) {
    return (
      <BaseFilePill
        fileName="Loading file..."
        fontType="text"
        icon={faFileSlash}
        variant="gray"
      />
    );
  }

  if (
    (error instanceof AxiosError && error.response?.status === 404) ||
    !file?.data.data?.attributes
  ) {
    return (
      <BaseFilePill
        fileName="File not found"
        fontType="text"
        icon={faFileSlash}
        variant="gray"
      />
    );
  }

  if (error) {
    return (
      <BaseFilePill
        fileName="Error fetching file"
        fontType="text"
        icon={faFileSlash}
        variant="red"
      />
    );
  }

  return (
    <BaseFilePill
      fileName={getFileName(file.data.data)}
      fontType="text"
      icon={faFileSlash}
      variant="gray"
    />
  );
};

export const FileCategoriesUpdated: React.FC<{
  entry: CaseHistoryEvent<"file_categories_updated">;
  isLast: boolean;
}> = ({ entry, isLast }) => {
  const { added_files, removed_files, updated_by_user_id } = entry.event_data;
  const capabilities = useCapabilities();
  const { case_id: caseId } = useParamsDecode<CasePageParams>();
  const { data: caseData } = useCase(caseId);

  const fileEnv =
    caseData?.data.env === "sandbox"
      ? GetFileEnvFilesFileIdGetEnvEnum.SANDBOX
      : GetFileEnvFilesFileIdGetEnvEnum.LIVE;

  const categoryDisplayNames = useMemo(() => {
    if (!caseData?.data.file_categories) return {};
    return caseData.data.file_categories.reduce(
      (acc, cat) => ({
        ...acc,
        [cat.category_key]: cat.display_name,
      }),
      {} as Record<string, string>,
    );
  }, [caseData?.data.file_categories]);

  const operation = getFileOperation(added_files, removed_files);

  const icon = operation.type === "deleted" ? faFileSlash : faFile;

  const headline = (() => {
    switch (operation.type) {
      case "uploaded":
        return (
          <>
            <FilePill
              env={fileEnv}
              fileId={operation.fileId}
              monospaced={false}
            />{" "}
            was uploaded to the case.
          </>
        );
      case "moved":
        return (
          <>
            <FilePill
              env={fileEnv}
              fileId={operation.fileId}
              monospaced={false}
            />{" "}
            was moved to{" "}
            <Pill size="sm" variant="gray">
              <Pill.Icon icon={faFiles} />
              <Pill.Text>
                {categoryDisplayNames[operation.toCategoryKey] ||
                  operation.toCategoryKey}
              </Pill.Text>
            </Pill>
            .
          </>
        );
      case "deleted":
        return (
          <>
            {capabilities.caseHistory.canSeeDetails ? (
              <FilePill
                env={fileEnv}
                fileId={operation.fileId}
                monospaced={false}
              />
            ) : (
              <DeletedFilePill env={fileEnv} fileId={operation.fileId} />
            )}{" "}
            was removed from the case.
          </>
        );
    }
  })();

  return (
    <HistorySingleEntry
      actorId={updated_by_user_id ?? undefined}
      eventType={entry.event_type}
      headline={headline}
      icon={icon}
      isLast={isLast}
      timestamp={entry.timestamp}
    />
  );
};

type FileOperationData =
  | { type: "uploaded"; fileId: string }
  | { type: "moved"; fileId: string; toCategoryKey: string }
  | { type: "deleted"; fileId: string };

const getFileOperation = (
  addedFiles: Record<string, string[]>,
  removedFiles: Record<string, string[]>,
): FileOperationData => {
  const addedFileId = Object.values(addedFiles).flat()[0];
  const removedFileId = Object.values(removedFiles).flat()[0];

  if (addedFileId && removedFileId) {
    // File was moved
    const toCategoryKey = Object.keys(addedFiles)[0];
    return {
      type: "moved",
      fileId: addedFileId,
      toCategoryKey,
    };
  }

  if (addedFileId) {
    // File was uploaded
    return {
      type: "uploaded",
      fileId: addedFileId,
    };
  }

  // File was deleted
  return {
    type: "deleted",
    fileId: removedFileId,
  };
};
